<template>
  <v-card class="ps-card" flat>
    <div class="ps-card__head">
      <v-icon small>mdi-creation</v-icon>
      AI-API-Kosten
      <span class="ps-card__hint">getrackte API-Keys — Kosten erscheinen als „KI-APIs" in der Statistik (Sync alle 15 Min)</span>
      <v-spacer />
      <v-btn @click="openDialog(null)" color="primary" depressed small rounded>
        <v-icon small left>mdi-plus</v-icon>API-Key
      </v-btn>
    </div>
    <v-card-text>
      <v-data-table
        :headers="headers"
        :items="keyRows"
        class="ps-table"
        hide-default-footer
        :items-per-page="-1"
      >
        <template v-slot:item.name="{ item }">
          <span class="ps-name">{{ item.name }}</span>
          <div v-if="item.description" class="ps-sub">{{ item.description }}</div>
        </template>
        <template v-slot:item.provider="{ item }">
          <span class="ps-chip">{{ providerLabel(item.provider) }}</span>
        </template>
        <template v-slot:item.key="{ item }">
          <span class="ai-key-mask">•••• {{ item.keyLast4 || "????" }}</span>
        </template>
        <template v-slot:item.status="{ item }">
          <span v-if="item.provider === 'gemini'" class="ai-status ai-status--ok">
            <v-icon x-small color="#2f7d52">mdi-lightning-bolt-outline</v-icon>
            trackt live je Aufruf
          </span>
          <span v-else-if="item.lastError" class="ai-status ai-status--error" :title="item.lastError">
            <v-icon x-small color="#b71c1c">mdi-alert-circle-outline</v-icon>
            {{ item.lastError }}
          </span>
          <span v-else-if="item.lastSyncAt" class="ai-status ai-status--ok">
            <v-icon x-small color="#2f7d52">mdi-check-circle-outline</v-icon>
            Sync {{ timeAgo(item.lastSyncAt) }}
          </span>
          <span v-else class="ps-muted">noch kein Sync</span>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-icon small class="mr-2" @click="() => openDialog(item)">mdi-pencil</v-icon>
          <v-icon small @click="() => (keyToDelete = item)">mdi-delete-outline</v-icon>
        </template>
      </v-data-table>
      <div v-if="!keyRows.length" class="ps-muted mt-2">
        Noch keine API-Keys hinterlegt. Hinweis: Für Claude und OpenAI wird der
        <strong>Admin-Key der Organisation</strong> benötigt (normale API-Keys
        dürfen die Kosten-Endpunkte nicht lesen).
      </div>
    </v-card-text>

    <!-- Anlegen / Bearbeiten -->
    <v-dialog v-model="showDialog" max-width="480">
      <v-card class="ps-dialog pa-5">
        <div class="ai-dialog-title">{{ editingKeyId ? "API-Key bearbeiten" : "API-Key hinzufügen" }}</div>
        <v-text-field v-model="form.name" label="Name" outlined dense hide-details class="mt-4" />
        <v-text-field v-model="form.description" label="Nutzungsbeschreibung" outlined dense hide-details class="mt-3" />
        <v-select
          v-model="form.provider"
          :items="providerOptions"
          label="Anbieter"
          outlined dense hide-details
          class="mt-3"
          :menu-props="{ bottom: true, offsetY: true }"
        />
        <v-text-field
          v-model="form.apiKey"
          :label="editingKeyId ? 'Neuer API-Key (leer = unverändert)' : 'API-Key'"
          type="password"
          autocomplete="new-password"
          outlined dense
          class="mt-3"
          :hint="providerKeyHint"
          persistent-hint
        />
        <template v-if="form.provider === 'gemini'">
          <!-- Gemini: kein Kosten-API bei Google — Token-basiertes
               Eigen-Tracking; der Key dient dem Hash-Abgleich an den
               Einsatzstellen (wird nie im Klartext verglichen) -->
          <v-row dense class="mt-3">
            <v-col cols="6">
              <v-text-field
                v-model.number="form.priceInPer1M"
                label="€ je 1M Input-Tokens"
                type="number" step="0.01"
                outlined dense hide-details
              />
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model.number="form.priceOutPer1M"
                label="€ je 1M Output-Tokens"
                type="number" step="0.01"
                outlined dense hide-details
              />
            </v-col>
          </v-row>
          <div class="ai-gemini-hint">
            Jeder Gemini-Aufruf mit genau diesem Key bucht seine Tokens
            (usageMetadata) × Preisliste — Bild-Ausgaben zählen als
            Output-Tokens (~1290 Tokens/Bild). Preise laut Google-Preisliste
            des genutzten Modells eintragen.
          </div>
        </template>
        <div v-if="error" class="ai-error">{{ error }}</div>
        <div class="d-flex justify-end mt-4" style="gap: 8px">
          <v-btn text small @click="showDialog = false">Abbrechen</v-btn>
          <v-btn small color="success" depressed :loading="saving" @click="save">Speichern</v-btn>
        </div>
      </v-card>
    </v-dialog>

    <ConfirmDialog
      :show="!!keyToDelete"
      :on-cancel="() => (keyToDelete = null)"
      :on-accept="confirmDelete"
      icon="mdi-trash-can-outline"
      title="API-Key löschen"
      :message="`„${keyToDelete && keyToDelete.name}“ wird nicht mehr getrackt und der hinterlegte Key gelöscht. Bereits erfasste Kosten bleiben in der Statistik. Fortfahren?`"
      accept-text="LÖSCHEN"
      accept-color="error"
    />
  </v-card>
</template>

<script>
import { httpsCallable } from "firebase/functions";
import { functions } from "../../../../fb";
import ConfirmDialog from "@/components/ConfirmDialog";
import { getDateFromNow } from "@/utils";

const PROVIDERS = [
  { value: "claude", text: "Claude (Anthropic)" },
  { value: "openai", text: "OpenAI" },
  { value: "gemini", text: "Gemini (Google)" },
  { value: "atlascloud", text: "Atlas Cloud" },
];

export default {
  name: "AiApiKeys",
  components: { ConfirmDialog },
  props: {
    settings: { type: Object, default: null },
  },
  data() {
    return {
      showDialog: false,
      editingKeyId: null,
      form: { name: "", description: "", provider: "claude", apiKey: "", priceInPer1M: 0.3, priceOutPer1M: 2.5 },
      saving: false,
      error: "",
      keyToDelete: null,
      providerOptions: PROVIDERS,
      headers: [
        { text: "Name", value: "name" },
        { text: "Anbieter", value: "provider", sortable: false },
        { text: "Key", value: "key", sortable: false },
        { text: "Status", value: "status", sortable: false },
        { text: "", value: "actions", sortable: false, align: "end" },
      ],
    };
  },
  computed: {
    keyRows() {
      const keys = this.settings?.aiKeys || {};
      return Object.entries(keys)
        .map(([keyId, meta]) => ({ keyId, ...meta }))
        .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    },
    providerKeyHint() {
      if (this.form.provider === "claude") return "Admin-Key der Organisation nötig (sk-ant-admin…)";
      if (this.form.provider === "openai") return "Admin-Key der Organisation nötig (sk-admin…)";
      if (this.form.provider === "gemini") return "Genau der Key, den die Functions nutzen — dient der Zuordnung per Hash-Abgleich";
      return "Automatischer Kosten-Abruf für Atlas Cloud noch nicht unterstützt";
    },
  },
  methods: {
    providerLabel(value) {
      return PROVIDERS.find((p) => p.value === value)?.text || value;
    },

    timeAgo(timestamp) {
      return getDateFromNow(timestamp);
    },
    openDialog(item) {
      this.editingKeyId = item?.keyId || null;
      this.form = {
        name: item?.name || "",
        description: item?.description || "",
        provider: item?.provider || "claude",
        apiKey: "",
        priceInPer1M: item?.priceInPer1M != null ? item.priceInPer1M : 0.3,
        priceOutPer1M: item?.priceOutPer1M != null ? item.priceOutPer1M : 2.5,
      };
      this.error = "";
      this.showDialog = true;
    },
    async save() {
      this.error = "";
      this.saving = true;
      try {
        const manage = httpsCallable(functions, "manageAiApiKey");
        const { data } = await manage({
          action: "save",
          keyId: this.editingKeyId || undefined,
          name: this.form.name,
          description: this.form.description,
          provider: this.form.provider,
          apiKey: this.form.apiKey || undefined,
          ...(this.form.provider === "gemini"
            ? {
                priceInPer1M: this.form.priceInPer1M,
                priceOutPer1M: this.form.priceOutPer1M,
              }
            : {}),
        });
        if (data?.ok === false || data?.status === "error") {
          this.error = data.error || data.message || "Speichern fehlgeschlagen";
          return;
        }
        this.showDialog = false;
      } catch (e) {
        this.error = e.message || "Speichern fehlgeschlagen";
      } finally {
        this.saving = false;
      }
    },
    async confirmDelete() {
      const item = this.keyToDelete;
      this.keyToDelete = null;
      const manage = httpsCallable(functions, "manageAiApiKey");
      await manage({ action: "delete", keyId: item.keyId });
    },
  },
};
</script>

<!-- ps-Design-System auch ohne Besuch der Produkte-Seite verfügbar -->
<style lang="scss">
@import "@/components/Settings/Products/product-settings.scss";
</style>

<style lang="scss" scoped>
.ai-key-mask {
  font-family: monospace;
  font-size: 12.5px;
  color: rgba(0, 0, 0, 0.6);
}

.ai-status {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  max-width: 260px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  &--ok {
    color: #2f7d52;
  }

  &--error {
    color: #b71c1c;
  }
}

.ai-gemini-hint {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.5);
  margin-top: 10px;
}

.ai-dialog-title {
  font-size: 16px;
  font-weight: 700;
}

.ai-error {
  color: #b71c1c;
  font-size: 12.5px;
  margin-top: 8px;
}
</style>
